import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from 'react';
import { authAPI } from '../api/axios';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const hasChecked = useRef(false); // Prevent double check in StrictMode

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('token');

    // If no token, done immediately
    if (!token || token === 'null' || token === 'undefined') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }

    // SECURITY: Validate token format before using
    if (token.length > 1000 || token.split('.').length !== 3) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }

    // Try to restore user from localStorage first (instant UI)
    try {
      const savedUser = localStorage.getItem('user');
      if (savedUser && savedUser !== 'undefined') {
        const parsed = JSON.parse(savedUser);
        // SECURITY: Validate parsed data has expected structure
        if (
          parsed &&
          typeof parsed === 'object' &&
          parsed._id &&
          typeof parsed._id === 'string' &&
          parsed.email &&
          typeof parsed.email === 'string' &&
          parsed.role &&
          ['admin', 'staff', 'superadmin'].includes(parsed.role)
        ) {
          setUser(parsed);
          setIsAuthenticated(true);
        } else {
          // SECURITY: Clear malformed/tampered user data
          localStorage.removeItem('user');
        }
      }
    } catch (e) {
      console.error("Corrupted local storage data", e);
      localStorage.removeItem('user');
    }

    // Verify token with server
    try {
      const response = await authAPI.getMe();
      const serverUser = response.data.data;
      setUser(serverUser);
      setIsAuthenticated(true);
      localStorage.setItem('user', JSON.stringify(serverUser));
    } catch (error) {
      // Only clear auth if it's a 401 (unauthorized), NOT on network errors or rate limits
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setIsAuthenticated(false);
      }
      // For 429 or network errors, keep the cached user data
      // so the app doesn't break
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    if (hasChecked.current) return;
    hasChecked.current = true;
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { token, ...userData } = response.data.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);
    return response.data;
  };

  // ⚠️ KEY FIX: No more window.location.href (which causes full page reload)
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    // Don't use window.location.href — it causes full reload → re-fetches everything → rate limit
    // The ProtectedRoute component will automatically redirect to /login
  }, []);

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAuthenticated,
      login,
      logout,
      updateUser,
      checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;